// QuickGuide.js
import { useEffect, useState } from "react";
import { useAuth } from "../Context/AuthContext";
import { db } from "../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import "../styles/QuickGuide.css";
import { useNavigate } from "react-router-dom";
import { getPayrollConfigured } from "../utils/firstRunFlags";

import { FaCheckCircle, FaHourglassHalf } from "react-icons/fa";

export default function QuickGuide({ onTabChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");

  const [progress, setProgress] = useState({
    addedEmployee: false,
    setNotifications: false,
    createdSchedule: false,
    configuredTimeTracking: false, // ✅ NEW
    configuredPayroll: false, // ✅ NEW
  });

  useEffect(() => {
    const fetchProgress = async () => {
      if (!user?.companyId) return;
      const companyRef = doc(db, "companies", user.companyId);
      const snap = await getDoc(companyRef);
      const data = snap.data();
      if (data.quickSetupProgress) {
        setProgress(data.quickSetupProgress);
      }

      if (data.name) {
        setCompanyName(data.name);

        // ✅ NEW: first-run flags (time tracking configured)
        const flagsRef = doc(
          db,
          "companies",
          user.companyId,
          "firstRun",
          "flags",
        );
        const flagsSnap = await getDoc(flagsRef);
        const flags = flagsSnap.exists() ? flagsSnap.data() : {};

        // Payroll configured can be tracked either via flags doc OR helper (we'll support both)
        let payrollConfigured = false;
        try {
          payrollConfigured = await getPayrollConfigured(db, user.companyId);
        } catch (e) {
          // fallback to flags doc if helper fails for any reason
          payrollConfigured = !!flags?.payrollConfigured;
        }

        setProgress((prev) => ({
          ...prev,
          configuredTimeTracking: !!flags?.timeTrackingConfigured,
          configuredPayroll: payrollConfigured, // ✅ NEW
        }));
      }
    };

    fetchProgress();
  }, [user]);

  const checklist = [
    {
      key: "setNotifications",
      label: "Set your notification preferences",
      tooltip: "Choose which alerts your company receives",
      path: "/settings",
    },
    {
      key: "addedEmployee",
      label: "Add your first employee",
      tooltip: "Start by creating an employee profile",
      tab: "employee-management",
    },

    {
      key: "configuredTimeTracking",
      label: "Configure time tracking settings",
      tooltip: "Set early clock-in, late grace, and overtime rules",
      tab: "attendance", // ✅ takes them right where Settings lives
    },
    {
      key: "configuredPayroll",
      label: "Configure payroll settings",
      tooltip: "Set your pay schedule, anchor date, and overtime pay rules",
      tab: "Payroll", // ✅ use whatever your Payroll route is
    },
  ];

  const completedCount = checklist.filter((item) => progress[item.key]).length;
  const percentage = Math.round((completedCount / checklist.length) * 100);

  return (
    <div className="quick-guide-container">
      <h2 className="quick-guide-title">
        Welcome to Oiiko{companyName ? `, ${companyName}` : ""}
      </h2>

      <p className="quick-guide-subtext">
        Complete these essentials to get the most out of Oiiko.
      </p>
      <div className="progress-summary-container">
        <div className="progress-summary">
          <span className="progress-percentage">
            Setup progress · {percentage}%
          </span>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
        </div>

        <ul className="checklist">
          {checklist.map((item) => (
            <li
              key={item.key}
              className={progress[item.key] ? "done" : "pending"}
              onClick={() => {
                if (item.path) {
                  navigate(item.path);
                } else if (item.tab) {
                  onTabChange(item.tab);
                }
              }}
              title={item.tooltip}
            >
              <span className="icon">
                {progress[item.key] ? (
                  <FaCheckCircle className="icon-done" />
                ) : (
                  <FaHourglassHalf className="icon-pending" />
                )}
              </span>
              <span className="label">{item.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
